import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2, Plus, ChevronUp, ChevronDown, Link2, Check, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

/** One question row of the QuizEditor (text, answers + correct flags, optional help link). */
export default function QuizQuestionCard({ question, index, total, onChange, onRemove, onMove }) {
  const { t } = useTranslation();
  const [linkOpen, setLinkOpen] = useState(!!question.link_url);
  const answers = question.answers || [];
  const correctCount = answers.filter((a) => a.correct).length;
  const invalid = answers.length < 2 || correctCount === 0 || !question.text?.trim();

  function patchAnswer(i, p) {
    onChange({ answers: answers.map((a, j) => (j === i ? { ...a, ...p } : a)) });
  }
  function setCorrect(i, v) {
    if (question.multiple) return patchAnswer(i, { correct: v });
    onChange({ answers: answers.map((a, j) => ({ ...a, correct: j === i ? v : false })) });
  }
  function setMultiple(v) {
    const next = { multiple: v };
    if (!v && correctCount > 1) {
      const first = answers.findIndex((a) => a.correct);
      next.answers = answers.map((a, j) => ({ ...a, correct: j === first }));
    }
    onChange(next);
  }

  return (
    <div
      className={`rounded-2xl border p-4 space-y-3 ${invalid ? "border-amber-400/60" : "border-border/70"}`}
      data-testid={`quiz-question-card-${index}`}
    >
      <div className="flex items-center gap-2">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground flex-1">
          {t("game_editor.quiz_question_n", { n: index + 1 })}
        </div>
        <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={index === 0}
          onClick={() => onMove(-1)} data-testid={`quiz-question-up-${index}`}>
          <ChevronUp className="h-4 w-4" />
        </Button>
        <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={index === total - 1}
          onClick={() => onMove(1)} data-testid={`quiz-question-down-${index}`}>
          <ChevronDown className="h-4 w-4" />
        </Button>
        <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive"
          onClick={onRemove} data-testid={`quiz-question-remove-${index}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <Textarea
        value={question.text || ""}
        onChange={(e) => onChange({ text: e.target.value })}
        rows={2}
        placeholder={t("game_editor.quiz_question_ph")}
        className="rounded-xl text-sm"
        data-testid={`quiz-question-text-${index}`}
      />

      <label className="flex items-center justify-between gap-3 rounded-xl border border-border/60 px-3 py-2 cursor-pointer">
        <div className="text-xs">
          <div className="font-semibold">{t("game_editor.quiz_multiple")}</div>
          <div className="text-[11px] text-muted-foreground">{t("game_editor.quiz_multiple_hint")}</div>
        </div>
        <Switch checked={!!question.multiple} onCheckedChange={setMultiple} data-testid={`quiz-question-multiple-${index}`} />
      </label>

      <div className="space-y-2">
        <Label className="text-[11px] font-medium text-muted-foreground">{t("game_editor.quiz_answers_label")}</Label>
        {answers.map((a, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`quiz-answer-row-${index}-${i}`}>
            <button
              type="button"
              onClick={() => setCorrect(i, !a.correct)}
              title={t("game_editor.quiz_mark_correct")}
              aria-pressed={!!a.correct}
              className={`flex h-8 w-8 shrink-0 items-center justify-center border-2 transition-colors ${question.multiple ? "rounded-lg" : "rounded-full"} ${a.correct ? "bg-emerald-500 border-emerald-500 text-white" : "border-border text-transparent hover:border-emerald-400"}`}
              data-testid={`quiz-answer-correct-${index}-${i}`}
            >
              <Check className="h-4 w-4" strokeWidth={3} />
            </button>
            <Input
              value={a.text || ""}
              onChange={(e) => patchAnswer(i, { text: e.target.value })}
              placeholder={t("game_editor.quiz_answer_ph", { n: i + 1 })}
              className="rounded-xl h-9 text-sm"
              data-testid={`quiz-answer-text-${index}-${i}`}
            />
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive"
              onClick={() => onChange({ answers: answers.filter((_, j) => j !== i) })}
              data-testid={`quiz-answer-remove-${index}-${i}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        <Button type="button" size="sm" variant="outline" className="h-8 rounded-lg text-xs"
          onClick={() => onChange({ answers: [...answers, { text: "", correct: false }] })}
          data-testid={`quiz-add-answer-${index}`}>
          <Plus className="h-3 w-3 mr-1" />
          {t("game_editor.quiz_add_answer")}
        </Button>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setLinkOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-600 dark:text-violet-300 hover:underline"
          data-testid={`quiz-question-link-toggle-${index}`}
        >
          <Link2 className="h-3.5 w-3.5" />
          {t("game_editor.quiz_link_toggle")}
        </button>
        {linkOpen && (
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input value={question.link_label || ""} onChange={(e) => onChange({ link_label: e.target.value })}
              placeholder={t("game_editor.quiz_link_label_ph")} className="rounded-xl h-9 text-sm"
              data-testid={`quiz-question-link-label-${index}`} />
            <Input value={question.link_url || ""} onChange={(e) => onChange({ link_url: e.target.value })}
              placeholder="https://…" type="url" className="rounded-xl h-9 text-sm"
              data-testid={`quiz-question-link-url-${index}`} />
          </div>
        )}
      </div>

      {invalid && (
        <div className="inline-flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-300" data-testid={`quiz-question-warning-${index}`}>
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          {t("game_editor.quiz_question_invalid")}
        </div>
      )}
    </div>
  );
}
